/**
 * The butterflies over the clearing -- the visible half of the "butterflies"
 * happy place in shared/happy.js.  The zone and this swarm are constructed
 * from the same {x, y, z, r}, so the thing you can see and the thing you can
 * bank are one number.
 *
 * Deliberately NOT built through level/builder.js: the Builder merges
 * geometry per material and what comes out cannot be animated (its own
 * onUpdate comment says so), has colliders, and is counted by
 * tools/inspect.mjs -- all three wrong for a dozen moving insects that the
 * cat must fly straight through.  This is its own object3D that main.js adds
 * beside the level, not into it.
 *
 * Cost: two draw calls for the whole swarm -- one InstancedMesh per wing
 * side, every butterfly an instance of each.  Two rather than the one the
 * merge would give because the flap is a per-instance ROTATION of a whole
 * wing about the body line, and a single instance matrix cannot articulate
 * two halves; a vertex-shader flap could, and is a page of shader for the
 * privilege of saving one draw call of 14 instances.  No textures: the wing
 * pattern is vertex colour (dark at the root, so the two wing roots meeting
 * read as a body) times a per-instance colour, in the spirit of the level's
 * canvas-drawn detail maps.
 */
import * as THREE from "three";

const COUNT = 14;

/**
 * Garden butterflies: mostly whites (large and small white, the ones every
 * real garden actually has), a brimstone yellow or two, one orange.  The
 * orange is the HUD's own accent, which is a small joke and also why it never
 * clashes with the UI.  Bright and unlit (MeshBasicMaterial) on purpose: the
 * player FINDS this happy place by seeing it from the garden, ~14 m away
 * through the fog's near edge, and lit white at that range greys out.
 */
const COLOURS = [
  0xf2efe2, 0xf2efe2, 0xf2efe2, 0xefe6c8, 0xe8d774, 0xe2a73c,
];

/** Flight envelope, metres.  Heights are above the zone's y (the clearing
    floor): low enough to pounce at -- the zone's yTol is 1.6 -- high enough
    to catch the light over the grass tufts. */
const H_MIN = 0.35;
const H_MAX = 1.9;
const WANDER_R = 0.85;      // of the zone radius: keep the wandering inside it
const DRIFT_TOP = 0.8;      // m/s wandering -- a flutter, not a bee-line
const SCATTER_TOP = 2.6;    // m/s fleeing a pounce
const FLAP_HZ = 9;          // wingbeats/s; whites measured at 8-12

/**
 * One wing, hinge at the origin, spanning +x: fore lobe up-and-out, hind lobe
 * smaller and swept back.  ~8.5 cm from hinge to tip, so a 17 cm span --
 * roughly twice a real large white, because these have to read from the
 * garden and the cats themselves are toon-proportioned.
 */
function wingGeometry() {
  const s = new THREE.Shape();
  s.moveTo(0, 0.012);
  s.quadraticCurveTo(0.052, 0.052, 0.085, 0.038);   // fore lobe, out to the tip
  s.quadraticCurveTo(0.072, 0.006, 0.052, 0.000);   // trailing edge in
  s.quadraticCurveTo(0.058, -0.028, 0.036, -0.042); // hind lobe
  s.quadraticCurveTo(0.010, -0.030, 0, -0.014);
  const g = new THREE.ShapeGeometry(s, 5);
  // Root-dark vertex colour: the innermost centimetre shades to near-black,
  // and where the two wings meet those roots read as the body -- which is the
  // whole body budget, and why there is no third mesh for one.
  const pos = g.attributes.position;
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const v = 0.22 + 0.78 * Math.min(1, Math.max(0, (x - 0.004) / 0.028));
    col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = v;
  }
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  // Shape space is XY; fly in XZ.  This maps the wing flat with its leading
  // edge forward (+y -> -z), so local -z is "ahead" for the flap maths below.
  g.rotateX(-Math.PI / 2);
  return g;
}

export class Butterflies {
  /** `zone` is the butterflies row of shared/happy.js: { x, y, z, r }. */
  constructor({ x, y, z, r }) {
    this.centre = new THREE.Vector3(x, y, z);
    this.r = r;

    // Reduced motion calms the flight rather than removing it: the swarm is
    // how the happy place is FOUND, so it must stay visible -- it just drifts
    // and flaps at half speed and scatters without the burst.
    this.calm = false;
    try {
      this.calm = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    } catch {}

    const geomR = wingGeometry();
    const geomL = geomR.clone();
    geomL.scale(-1, 1, 1);        // mirror; winding flips, DoubleSide absorbs it
    const mat = new THREE.MeshBasicMaterial({
      vertexColors: true,
      side: THREE.DoubleSide,
    });
    this.wingR = new THREE.InstancedMesh(geomR, mat, COUNT);
    this.wingL = new THREE.InstancedMesh(geomL, mat, COUNT);
    for (const m of [this.wingR, this.wingL]) {
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      // The instances wander a 3-ish-metre disc but the geometry's own bounds
      // are one wing at the origin, so three's frustum test would cull the
      // whole swarm while butterflies were plainly on screen.  Never culling
      // 14 instances is cheaper than maintaining honest bounds every frame.
      m.frustumCulled = false;
      // No shadows: the swarm lives outside the 13 m shadow span that follows
      // the player anyway, and fourteen flickering wing shadows popping in at
      // its edge would be worse than none.
      m.castShadow = false;
      m.receiveShadow = false;
    }

    this.group = new THREE.Group();
    this.group.add(this.wingR, this.wingL);

    // Per-butterfly state, parallel arrays of one object each.
    const rng = Math.random;
    this.flock = [];
    for (let i = 0; i < COUNT; i++) {
      const b = {
        pos: this.spot(rng),
        vel: new THREE.Vector3(),
        tgt: this.spot(rng),
        retarget: rng() * 2,
        phase: rng() * Math.PI * 2,   // unsynchronised wingbeats
        flapHz: FLAP_HZ * (0.85 + rng() * 0.35),
        bobPhase: rng() * Math.PI * 2,
        size: 0.8 + rng() * 0.35,
        glide: 0,                     // s of wings-nearly-still coasting left
        scatter: 0,                   // s of fleeing left
      };
      this.flock.push(b);
      const c = new THREE.Color(COLOURS[(rng() * COLOURS.length) | 0]);
      this.wingR.setColorAt(i, c);
      this.wingL.setColorAt(i, c);
    }
    this.wingR.instanceColor.needsUpdate = true;
    this.wingL.instanceColor.needsUpdate = true;

    this.prevState = "";
    this.dummy = new THREE.Object3D();
    this.qYaw = new THREE.Quaternion();
    this.qRoll = new THREE.Quaternion();
    this.scratch = new THREE.Vector3();
    this.update(0.016, null, "idle");   // valid matrices before the first frame
  }

  /** Add this to the scene; it is not part of the level build. */
  get object3D() {
    return this.group;
  }

  /** A wander point inside the zone's flight envelope. */
  spot(rng) {
    const a = rng() * Math.PI * 2;
    const d = Math.sqrt(rng()) * this.r * WANDER_R;   // sqrt: even over the disc
    return new THREE.Vector3(
      this.centre.x + Math.cos(a) * d,
      this.centre.y + H_MIN + rng() * (H_MAX - H_MIN),
      this.centre.z + Math.sin(a) * d,
    );
  }

  /**
   * Every butterfly bursts away from the cat, out and up.  Fleeing UP is what
   * makes pouncing at them a game: they leave through where the cat's arc
   * peaks, so a second charged pounce right after the scatter is the best
   * shot, which is exactly the input the place's rule counts.
   */
  scatterFrom(p) {
    const burst = this.calm ? 0.35 : 1;
    for (const b of this.flock) {
      b.scatter = 1.4 + Math.random() * 0.9;
      b.glide = 0;
      this.scratch.copy(b.pos).sub(p);
      this.scratch.y = Math.max(this.scratch.y, 0) + 0.7;   // up and away
      this.scratch.normalize();
      b.vel.copy(this.scratch)
        .multiplyScalar((SCATTER_TOP * (0.7 + Math.random() * 0.3)) * burst);
    }
  }

  /**
   * dt seconds, the cat's position (or null) and its state string.  The
   * server counts the pounces that bank the place; this only has to make the
   * swarm REACT, which is the difference between playing with butterflies
   * and standing in a zone.
   */
  update(dt, catPos, catState) {
    dt = Math.min(dt || 0, 0.05);
    const calmF = this.calm ? 0.5 : 1;

    // Edge-triggered on leaving "pounce": that is the launch (the state IS
    // the charging crouch -- see player.js), so the swarm bursts as the cat
    // springs, not while it is still winding up.  A swipe close-in scatters
    // too; batting at them should not be ignored just because it is not the
    // input the server counts.
    if (catPos) {
      const near = catPos.distanceTo(this.centre) < this.r + 2.5;
      const launched = this.prevState === "pounce" && catState !== "pounce";
      const swiped = catState === "swipe" && this.prevState !== "swipe";
      if (near && (launched || swiped)) this.scatterFrom(catPos);
    }
    this.prevState = catState;

    for (let i = 0; i < COUNT; i++) {
      const b = this.flock[i];

      if (b.scatter > 0) {
        b.scatter -= dt;
        b.vel.multiplyScalar(Math.exp(-dt * 1.6));   // the burst spends itself
        if (b.scatter <= 0) b.retarget = 0;          // then regroup: new spot now
      } else {
        b.retarget -= dt;
        if (b.retarget <= 0) {
          b.tgt = this.spot(Math.random);
          b.retarget = 1.2 + Math.random() * 2.2;
          // Occasionally coast: wings held nearly still for half a second is
          // one of the two tells (with the bob) that this is a butterfly and
          // not a moth-shaped particle.
          if (Math.random() < 0.35) b.glide = 0.3 + Math.random() * 0.5;
        }
        this.scratch.copy(b.tgt).sub(b.pos).multiplyScalar(1.6 * calmF);
        b.vel.addScaledVector(this.scratch, dt);
        b.vel.multiplyScalar(Math.exp(-dt * 0.9));
        const top = DRIFT_TOP * calmF;
        if (b.vel.lengthSq() > top * top) b.vel.setLength(top);

        // A cat merely walking close shoulders them aside -- gently, so
        // stalking through the swarm parts it rather than detonating it.
        if (catPos && b.pos.distanceToSquared(catPos) < 0.9 * 0.9) {
          this.scratch.copy(b.pos).sub(catPos).setY(0.4).normalize();
          b.vel.addScaledVector(this.scratch, 1.5 * dt);
        }
      }

      b.pos.addScaledVector(b.vel, dt);

      // Leash to the zone: past 1.4r steer home, and hard-clamp at 1.7r so a
      // scatter can never strand one over the creek for good.  Heights clamp
      // to the envelope plus scatter headroom.
      const dx = b.pos.x - this.centre.x, dz = b.pos.z - this.centre.z;
      const planR = Math.hypot(dx, dz);
      if (planR > this.r * 1.4) {
        this.scratch.set(-dx, 0, -dz).normalize();
        b.vel.addScaledVector(this.scratch, 2.4 * dt);
        if (planR > this.r * 1.7) {
          const s = (this.r * 1.7) / planR;
          b.pos.x = this.centre.x + dx * s;
          b.pos.z = this.centre.z + dz * s;
        }
      }
      b.pos.y = Math.min(this.centre.y + H_MAX + 0.6,
                         Math.max(this.centre.y + H_MIN * 0.5, b.pos.y));

      // Wingbeat and bob.  The beat runs faster fleeing and slower gliding;
      // the bob is the flap's cause played back at the flap's rate -- the
      // body rides each downstroke -- plus a slow 1.3 Hz swim so even a
      // hovering butterfly is never still.
      const flapRate = b.scatter > 0 ? 1.8 : b.glide > 0 ? 0.25 : 1;
      if (b.glide > 0) b.glide -= dt;
      b.phase += Math.PI * 2 * b.flapHz * flapRate * calmF * dt;
      b.bobPhase += 1.3 * Math.PI * 2 * calmF * dt;
      const bob = Math.sin(b.phase) * 0.008 + Math.sin(b.bobPhase) * 0.03;

      // Flap angle: rest attitude 0.45 rad (wings up in a shallow V, how
      // whites actually hold them), swinging 0.75 rad each way -- almost
      // touching over the back at the top, just below flat at the bottom.
      const flap = b.glide > 0
        ? 0.2
        : 0.45 + Math.sin(b.phase) * 0.75;

      // Face travel.  Local -z is "ahead" (see wingGeometry), and below
      // walking pace the last heading is kept -- a hoverer that re-aimed
      // every frame off a near-zero velocity would spin like a compass.
      const speed2 = b.vel.x * b.vel.x + b.vel.z * b.vel.z;
      if (speed2 > 0.01) b.yaw = Math.atan2(-b.vel.x, -b.vel.z);
      this.qYaw.setFromAxisAngle(AXIS_Y, b.yaw ?? 0);

      const d = this.dummy;
      d.position.set(b.pos.x, b.pos.y + bob, b.pos.z);
      d.scale.setScalar(b.size);
      // Roll about the body line: +flap raises the right wingtip, and the
      // mirrored left geometry wants the opposite sign to rise with it.
      this.qRoll.setFromAxisAngle(AXIS_Z, flap);
      d.quaternion.copy(this.qYaw).multiply(this.qRoll);
      d.updateMatrix();
      this.wingR.setMatrixAt(i, d.matrix);
      this.qRoll.setFromAxisAngle(AXIS_Z, -flap);
      d.quaternion.copy(this.qYaw).multiply(this.qRoll);
      d.updateMatrix();
      this.wingL.setMatrixAt(i, d.matrix);
    }

    this.wingR.instanceMatrix.needsUpdate = true;
    this.wingL.instanceMatrix.needsUpdate = true;
  }
}

const AXIS_Y = new THREE.Vector3(0, 1, 0);
const AXIS_Z = new THREE.Vector3(0, 0, 1);
